import React, { useCallback, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Plus } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { PageContainer } from '@/components/layout/PageContainer'
import PlanList from '@/components/plans/PlanList'
import CreatePlanModal from '@/components/plans/CreatePlanModal'
import { planService } from '@/services/planService'
import { matchingService } from '@/services/matchingService'
import { useAuth } from '@/contexts/Auth0Context'
import { useToast } from '@/contexts/ToastContext'
import { Plan } from '@/types/plan'

const PlansPage: React.FC = () => {
  const { relationshipId } = useParams<{ relationshipId: string }>()
  const { user, isAuthenticated, isLoading: authLoading } = useAuth()
  const { showToast } = useToast()

  const [plans, setPlans] = useState<Plan[]>([])
  const [menteeId, setMenteeId] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)

  const isMentor = user?.role === 'mentor'

  const fetchPlans = useCallback(async () => {
    if (!relationshipId) return
    try {
      setLoading(true)
      const data = await planService.getPlansForRelationship(relationshipId)
      setPlans(Array.isArray(data) ? data : [])
    } catch (error) {
      showToast({
        type: 'error',
        message: error instanceof Error ? error.message : 'Failed to load plans'
      })
    } finally {
      setLoading(false)
    }
  }, [relationshipId, showToast])

  useEffect(() => {
    if (authLoading || !isAuthenticated) return
    fetchPlans()
  }, [authLoading, isAuthenticated, fetchPlans])

  // Resolve the mentee id for plan creation (needed by the modal)
  useEffect(() => {
    if (!isMentor || !relationshipId || authLoading || !isAuthenticated) return
    matchingService
      .getMyRelationships()
      .then((relationships) => {
        const relationship = relationships.find((r) => r.id === relationshipId)
        if (relationship) {
          setMenteeId(relationship.menteeId)
        }
      })
      .catch(() => undefined)
  }, [isMentor, relationshipId, authLoading, isAuthenticated])

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PageContainer maxWidth="7xl">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Plans</h1>
            <p className="mt-1 text-gray-600">Mentorship plans for this relationship</p>
          </div>
          {isMentor && (
            <Button onClick={() => setIsCreateModalOpen(true)}>
              <Plus className="w-4 h-4 mr-1" />
              Create Plan
            </Button>
          )}
        </div>

        <PlanList plans={plans} />

        {isMentor && relationshipId && (
          <CreatePlanModal
            isOpen={isCreateModalOpen}
            relationshipId={relationshipId}
            menteeId={menteeId}
            onClose={() => setIsCreateModalOpen(false)}
            onPlanCreated={() => fetchPlans()}
          />
        )}
      </PageContainer>
    </div>
  )
}

export default PlansPage
